import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { driver, type Driver, type DriveStep, type PopoverDOM } from 'driver.js';
import 'driver.js/dist/driver.css';
import './tour-theme.css';
import { filterNav, useActiveModelSurfaces, type NavNode } from '@trinetra/shared';
import { useAuth } from '../contexts/AuthContext';
import { useNode } from '../contexts/NodeContext';
import { ALL_SITES_NAV, SITE_NAV } from '../constants/navigation';
import { TOURS, resolveTourId, type TourDefinition, type TourLocation, type TourStep } from './tourDefinitions';
import { findPageTour } from './pages';
import GettingStartedModal, { type ChecklistRow } from './GettingStartedModal';

interface TourContextValue {
  /** Start (or replay) the current user's app tour. */
  startTour: () => void;
  /** Start (or replay) the tour of the page on screen, if it has one. */
  startPageTour: () => void;
  /** Name of the current page's tour, or null when this page has none. */
  pageTourTitle: string | null;
  /** Open the Getting started checklist. */
  openGettingStarted: () => void;
}

const TourContext = createContext<TourContextValue>({
  startTour: () => { },
  startPageTour: () => { },
  pageTourTitle: null,
  openGettingStarted: () => { },
});

export function useTour() {
  return useContext(TourContext);
}

/** Wait this long after the shell mounts before opening the checklist, so it lands on a settled page. */
const PROMPT_DELAY_MS = 1200;
/**
 * Which login the checklist has already opened for in this tab. sessionStorage is per tab and
 * survives in-app navigation and refresh, so the checklist comes back on every app open (new
 * tab) and every login (new token), but not on every page or reload. Keyed to the login rather
 * than cleared on unmount: React's StrictMode unmounts once on every load in development, which
 * cleared it and reopened the checklist on each refresh.
 */
const SHOWN_KEY = 'trinetra.gettingStarted.shownFor';

/** A short stand-in for the auth token — enough to tell logins apart without storing it twice. */
const loginMark = (token: string | null) => (token ? token.slice(-16) : '');

/** What every tour has in common — the app tours and the page tours both fit. */
interface AnyTour {
  id: string;
  version: number;
  steps: TourStep[];
}

/** One checklist row: a tour, and the page it runs on (null = the page already on screen). */
interface Entry {
  tour: AnyTour;
  title: string;
  summary: string;
  path: string | null;
  isPage: boolean;
}

/**
 * First matching element that is actually on screen. A size is not enough: the desktop sidebar is
 * display:none on small screens, and some panes fold by fading out rather than unmounting
 * (Search & Track keeps its filter column mounted at full width with `invisible opacity-0`), so a
 * tour that only measured would point at controls nobody can see. checkVisibility also follows
 * opacity and visibility up the tree; browsers without it fall back to the size check.
 */
function findVisible(selector: string): Element | null {
  const els = document.querySelectorAll(selector);
  for (const el of Array.from(els)) {
    const rect = el.getBoundingClientRect();
    if (el.getClientRects().length === 0 || rect.width <= 0 || rect.height <= 0) continue;
    const check = (el as Element & { checkVisibility?: (o: object) => boolean }).checkVisibility;
    if (check && !check.call(el, { opacityProperty: true, visibilityProperty: true })) continue;
    return el;
  }
  return null;
}

function permittedSteps(tour: AnyTour, hasPermission: (p: string) => boolean): TourStep[] {
  const at: TourLocation = {
    pathname: window.location.pathname,
    params: new URLSearchParams(window.location.search),
  };
  return tour.steps.filter((s) =>
    (!s.permission || hasPermission(s.permission)) &&
    (!s.anyPermission || s.anyPermission.some(hasPermission)) &&
    (!s.when || s.when(at)));
}

/**
 * Turn a tour definition into driver.js steps for THIS user on THIS screen: steps they lack the
 * permission for are dropped, and so is any step whose target isn't on screen right now (other
 * scope's menu, model not active on this site, empty list, narrow screen).
 */
function buildSteps(steps: TourStep[]): DriveStep[] {
  const out: DriveStep[] = [];
  for (const s of steps) {
    const popover = { title: s.title, description: s.description, side: s.side, align: 'start' as const };
    if (!s.target) {
      out.push({ popover });
      continue;
    }
    const el = findVisible(s.target);
    // Looked up again when the step is shown: a page that re-renders mid-tour (data arriving,
    // a list refreshing) replaces the node found here, and driver would point at a detached one.
    const target = s.target;
    if (el) out.push({ element: () => findVisible(target) ?? el, popover });
  }
  return out;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** A page counts as loaded this long after it was opened… */
const PAGE_SETTLE_MS = 500;
/** …and once which of its targets are on screen has stopped changing for this long. */
const QUIET_MS = 200;
const MAX_WAIT_MS = 6000;

/**
 * buildSteps once the page has SETTLED. Starting as soon as every target was found counted
 * controls that a page shows only while it loads — Search & Track's "Looking for" picker is on
 * screen until the site's search schema arrives, then goes — so the same tour came out 5 steps
 * from the checklist and 4 from the "?" menu. Now the set of visible targets has to hold still
 * for QUIET_MS, no earlier than PAGE_SETTLE_MS after the page was opened. A page that was already
 * settled when the tour was asked for ("?" on a page open for a while) starts at once.
 * `openedAt` is read on every check, not once: when the checklist navigates, this starts before
 * the route change is recorded.
 */
async function collectSteps(
  tour: AnyTour,
  hasPermission: (p: string) => boolean,
  openedAt: () => number,
): Promise<DriveStep[]> {
  const steps = permittedSteps(tour, hasPermission);
  const deadline = Date.now() + MAX_WAIT_MS;
  // Read after a tick, so a checklist Open's route change has been recorded.
  await sleep(0);
  if (Date.now() - openedAt() >= PAGE_SETTLE_MS * 2) return buildSteps(steps);
  let lastKey = '';
  let changedAt = Date.now();
  for (;;) {
    const key = steps.map((s) => (!s.target || findVisible(s.target) ? '1' : '0')).join('');
    if (key !== lastKey) {
      lastKey = key;
      changedAt = Date.now();
    }
    const now = Date.now();
    if ((now - openedAt() >= PAGE_SETTLE_MS && now - changedAt >= QUIET_MS) || now >= deadline) {
      return buildSteps(steps);
    }
    await sleep(50);
  }
}

/** Every leaf link of a (filtered) nav tree, in menu order. */
function navLinks(items: NavNode[]): string[] {
  return items.flatMap((n) => (n.children ? navLinks(n.children) : n.to ? [n.to] : []));
}

/** Small DOM helper for the extras added to driver.js's popover. */
function el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text) node.textContent = text;
  return node;
}

/**
 * Owns the guided tours for the authenticated shell:
 *  - the Getting started checklist — every page in the current menu that has a tour, plus the
 *    role's app tour — opened on every app open and from the "?" menu / sidebar;
 *  - the tours themselves, one at a time or chained through every unfinished row.
 * Done/not-done is saved on the server (cloud_users.tour_state). Mounted in Layout so the
 * header and sidebar the app tour points at are always present.
 */
export function TourProvider({ children }: { children: ReactNode }) {
  const { user, token, loading, hasPermission, markTourSeen } = useAuth();
  const { selectedNode, selectedNodeId, nodeConnecting } = useNode();
  const { hasModel } = useActiveModelSurfaces(selectedNode?.nodeId ?? null);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const driverRef = useRef<Driver | null>(null);
  /** Whether the tour on screen is a page tour — those end when the user leaves the page. */
  const runningPageTourRef = useRef(false);
  /** Set while the shell unmounts (logout), so tearing the overlay down isn't saved as a skip. */
  const unmountingRef = useRef(false);
  /** Rows still to run after the current one, when "Start the tour" is chaining. */
  const queueRef = useRef<Entry[]>([]);
  /** A tour waiting for navigation to its page to finish. */
  const pendingRef = useRef<Entry | null>(null);
  /** When the page on screen was opened — collectSteps waits for it to settle. */
  const pageOpenedAtRef = useRef(Date.now());
  /** startEntry, for runEntry's chaining — the two depend on each other. */
  const startEntryRef = useRef<(entry: Entry) => void>(() => { });

  const appTour = useMemo<TourDefinition | null>(
    () => (user ? TOURS[resolveTourId(user.role, hasPermission)] : null),
    // hasPermission is recreated each render; the inputs it reads are these.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.role, user?.permissions],
  );
  const pageTour = useMemo(() => findPageTour(pathname), [pathname]);
  const tourState = user?.tour_state;

  // The checklist mirrors the sidebar: the same scope's tree, filtered the same way.
  const entries = useMemo<Entry[]>(() => {
    if (!user) return [];
    const out: Entry[] = [];
    if (appTour) {
      out.push({
        tour: appTour,
        title: 'Find your way around',
        summary: 'The top bar, the side menu, alerts and your account — where everything lives.',
        path: null,
        isPage: false,
      });
    }
    const nav = selectedNodeId
      ? filterNav(SITE_NAV, hasPermission, hasModel)
      : filterNav(ALL_SITES_NAV, hasPermission);
    const seen = new Set<string>();
    for (const to of navLinks(nav)) {
      const t = findPageTour(to);
      // One row per tour: every per-model report shares the Reports tour.
      if (!t || seen.has(t.id)) continue;
      seen.add(t.id);
      out.push({
        tour: t,
        title: t.title,
        summary: t.summary ?? t.steps[0]?.description ?? '',
        path: to,
        isPage: true,
      });
    }
    return out;
    // hasPermission/hasModel are recreated each render; the inputs they read are these.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, user?.permissions, appTour, selectedNodeId, hasModel]);

  const isDone = useCallback(
    (t: Pick<AnyTour, 'id' | 'version'>) =>
      tourState?.[t.id]?.status === 'completed' && (tourState[t.id]?.version ?? 0) >= t.version,
    [tourState],
  );

  const rows = useMemo<ChecklistRow[]>(
    () => entries.map((e) => ({ id: e.tour.id, title: e.title, summary: e.summary, done: isDone(e.tour) })),
    [entries, isDone],
  );

  const save = useCallback((t: Pick<AnyTour, 'id' | 'version'>, status: 'completed' | 'skipped') => {
    markTourSeen(t.id, t.version, status).catch((err) => {
      console.warn('Could not save tour state', err);
    });
  }, [markTourSeen]);

  const openGettingStarted = useCallback(() => {
    driverRef.current?.destroy();
    setChecklistOpen(true);
  }, []);

  /** Run a tour now. `onEnd` gets whether the user reached the end. */
  const run = useCallback((steps: DriveStep[], opts: { doneText: string; isPage: boolean; onEnd: (completed: boolean) => void }) => {
    driverRef.current?.destroy();
    if (steps.length === 0) return;

    let finished = false;
    // "A" jumps back to the checklist and Enter means Next, as the hint in the popover says.
    // Listened to in the capture phase: driver.js focuses the popover's first button (the ✕), so
    // left alone Enter would press ✕ and end the tour.
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'Enter') {
        const nextBtn = document.querySelector<HTMLButtonElement>('.driver-popover.trinetra-tour .driver-popover-next-btn');
        if (!nextBtn || (e.target as Element | null)?.closest?.('.trinetra-tour-all')) return;
        e.preventDefault();
        e.stopPropagation();
        nextBtn.click();
        return;
      }
      if (e.key === 'a' || e.key === 'A') {
        e.preventDefault();
        openGettingStarted();
      }
    };
    const d = driver({
      steps,
      popoverClass: 'trinetra-tour',
      showProgress: steps.length > 1,
      progressText: '{{current}} of {{total}}',
      // Back stays on the ← key; one button keeps the footer as calm as the reference design.
      // 'previous' must still be listed or driver.js ignores ← — the CSS hides the button.
      showButtons: ['next', 'previous', 'close'],
      nextBtnText: 'Next',
      doneBtnText: opts.doneText,
      overlayColor: '#000',
      overlayOpacity: 0.55,
      stagePadding: 6,
      stageRadius: 8,
      smoothScroll: true,
      allowClose: true,
      // Look, don't touch: a click on a highlighted link would navigate away mid-tour.
      disableActiveInteraction: true,
      onPopoverRender: (popover: PopoverDOM, { state }) => {
        // Step counter above the title, as an eyebrow.
        popover.wrapper.insertBefore(popover.progress, popover.title);

        const extras = el('div', 'trinetra-tour-extras');
        const all = el('button', 'trinetra-tour-all', 'All tours');
        all.type = 'button';
        all.addEventListener('click', openGettingStarted);
        extras.appendChild(all);
        const bar = el('div', 'trinetra-tour-progress');
        const fill = el('div', 'trinetra-tour-progress-fill');
        fill.style.width = `${(((state.activeIndex ?? 0) + 1) / steps.length) * 100}%`;
        bar.appendChild(fill);
        extras.appendChild(bar);
        popover.description.after(extras);

        popover.footer.insertBefore(
          el('span', 'trinetra-tour-hint', '→ or Enter next · ← back · A all tours · Esc ends'),
          popover.footer.firstChild,
        );
      },
      onDoneClick: () => {
        finished = true;
        d.destroy();
      },
      onDestroyed: () => {
        document.removeEventListener('keydown', onKey, true);
        if (driverRef.current === d) {
          driverRef.current = null;
          runningPageTourRef.current = false;
          setRunning(false);
        }
        if (!unmountingRef.current) opts.onEnd(finished);
      },
    });
    driverRef.current = d;
    runningPageTourRef.current = opts.isPage;
    setRunning(true);
    document.addEventListener('keydown', onKey, true);
    d.drive();
  }, [openGettingStarted]);

  // ── Running a row ─────────────────────────────────────────────

  /** Run an entry's tour on the page now on screen, then carry on with the queue if any. */
  const runEntry = useCallback(async (entry: Entry) => {
    let steps = entry.isPage
      ? await collectSteps(entry.tour, hasPermission, () => pageOpenedAtRef.current)
      : buildSteps(permittedSteps(entry.tour, hasPermission));
    // The user may have moved on while the page was still loading.
    if (entry.isPage && findPageTour(window.location.pathname)?.id !== entry.tour.id) return;

    const advance = () => {
      const next = queueRef.current.shift();
      if (next) startEntryRef.current(next);
    };
    const more = queueRef.current.length > 0;
    if (steps.length === 0) {
      // Nothing of this page is on screen (no data yet, site offline). In a chain, move on rather
      // than stall; asked for directly, say so — an Open that does nothing reads as broken.
      if (more) {
        advance();
        return;
      }
      steps = [{
        popover: {
          title: entry.title,
          description: 'There is nothing on this page to point at yet — its controls appear once it has data. Try this tour again then.',
        },
      }];
    }
    run(steps, {
      doneText: more ? 'Next page →' : entry.isPage ? 'Got it' : 'Finish',
      isPage: entry.isPage,
      onEnd: (completed) => {
        if (!completed) {
          queueRef.current = [];
          return;
        }
        save(entry.tour, 'completed');
        advance();
      },
    });
  }, [hasPermission, run, save]);

  /** Go to the entry's page if we aren't on it, then run its tour once the page is there. */
  const startEntry = useCallback((entry: Entry) => {
    setChecklistOpen(false);
    if (entry.path && findPageTour(window.location.pathname)?.id !== entry.tour.id) {
      pendingRef.current = entry;
      navigate(entry.path);
      return;
    }
    void runEntry(entry);
  }, [navigate, runEntry]);
  startEntryRef.current = startEntry;

  // Navigation for a pending tour has landed: run it.
  useEffect(() => {
    const entry = pendingRef.current;
    if (!entry || findPageTour(pathname)?.id !== entry.tour.id) return;
    pendingRef.current = null;
    void runEntry(entry);
  }, [pathname, runEntry]);

  // A page tour belongs to its page: leaving the page (back button, a link in a notification)
  // ends it rather than leaving it pointing at controls that no longer exist.
  useEffect(() => {
    pageOpenedAtRef.current = Date.now();
    if (runningPageTourRef.current) driverRef.current?.destroy();
  }, [pathname]);

  // ── Checklist ─────────────────────────────────────────────────

  // Open it on every app open — once per tab, after /auth/me (loading) and any site-connecting
  // overlay have settled, so it lands on a page that is ready.
  useEffect(() => {
    if (loading || !user || nodeConnecting || running) return;
    const mark = loginMark(token);
    try { if (sessionStorage.getItem(SHOWN_KEY) === mark) return; } catch { /* private mode — show it */ }
    const timer = setTimeout(() => {
      try { sessionStorage.setItem(SHOWN_KEY, mark); } catch { /* ignore */ }
      setChecklistOpen(true);
    }, PROMPT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [loading, user, token, nodeConnecting, running]);

  const entryById = useCallback((id: string) => entries.find((e) => e.tour.id === id), [entries]);

  const onOpen = useCallback((id: string) => {
    const entry = entryById(id);
    if (!entry) return;
    queueRef.current = [];
    startEntry(entry);
  }, [entryById, startEntry]);

  const onStartAll = useCallback(() => {
    const todo = entries.filter((e) => !isDone(e.tour));
    const first = todo.shift();
    queueRef.current = todo;
    if (first) startEntry(first);
  }, [entries, isDone, startEntry]);

  // ── Header / "?" menu ─────────────────────────────────────────

  const startTour = useCallback(() => {
    const entry = entries.find((e) => !e.isPage);
    queueRef.current = [];
    if (entry) startEntry(entry);
  }, [entries, startEntry]);

  const startPageTour = useCallback(() => {
    if (!pageTour) return;
    queueRef.current = [];
    startEntry({ tour: pageTour, title: pageTour.title, summary: pageTour.summary ?? '', path: null, isPage: true });
  }, [pageTour, startEntry]);

  // Logout or leaving the shell mid-tour must not leave the overlay behind. Reset on mount too:
  // StrictMode mounts, unmounts and remounts in development.
  useEffect(() => {
    unmountingRef.current = false;
    return () => {
      unmountingRef.current = true;
      driverRef.current?.destroy();
    };
  }, []);

  const value = useMemo<TourContextValue>(() => ({
    startTour,
    startPageTour,
    pageTourTitle: pageTour?.title ?? null,
    openGettingStarted,
  }), [startTour, startPageTour, pageTour, openGettingStarted]);

  return (
    <TourContext.Provider value={value}>
      {children}
      <GettingStartedModal
        open={checklistOpen}
        rows={rows}
        note={selectedNodeId ? null : "Pick a site in the top bar to see that site's own pages here."}
        onOpen={onOpen}
        onStartAll={onStartAll}
        onHide={() => setChecklistOpen(false)}
      />
    </TourContext.Provider>
  );
}
